/**
 * DiceBear avatar utilities - JavaScript equivalent of ViewsShared::DicebearAvatars
 *
 * Generates deterministic avatar URLs using the DiceBear API.
 * Keep in sync with: components/views_shared/lib/views_shared/dicebear_avatars.rb
 */

// Application brand colors for avatar backgrounds (matches Ruby implementation).
// Exported so inline-fallback renderers (e.g. ImageCropped's initials swap) use the same palette.
export const APPLICATION_SHAPES_HEXES = [
  "f7aa9f", // coral-30
  "c7bff9", // periwinkle-30
  "a6bddd", // tbn-30
  "f5a5b3", // raspberry-30
];

const APPLICATION_SHAPES_COLORS = APPLICATION_SHAPES_HEXES.join(",");

// Inline-initials fallback pieces, shared by ImageCropped and AvatarWithLabel so a failed or
// missing avatar renders the same locally-drawn initials circle everywhere. Drawn client-side on
// purpose: the DiceBear default is itself an off-site fetch, so it can't be the recovery path.
const INITIALS_PALETTE = APPLICATION_SHAPES_HEXES.map((hex) => `#${hex}`);

export function getInitials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function initialsPaletteColor(name) {
  const value = name || "";
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 2147483647;
  }
  return INITIALS_PALETTE[hash % INITIALS_PALETTE.length];
}

/**
 * Generates a DiceBear initials avatar URL for a given name.
 * Used for user/person avatars.
 *
 * @param {string} name - The name to generate avatar for (used as seed)
 * @returns {string} DiceBear avatar URL
 */
export function initialsAvatarUrl(name) {
  const encodedName = encodeURIComponent(name || "");
  return (
    `https://api.dicebear.com/7.x/initials/png?` +
    `backgroundColor=${APPLICATION_SHAPES_COLORS}&` +
    `fontFamily=sans-serif,Helvetica&` +
    `seed=${encodedName}`
  );
}

/**
 * Returns the avatar URL, falling back to a DiceBear initials avatar if not provided.
 *
 * @param {string|null} avatarUrl - The existing avatar URL (may be null/undefined)
 * @param {string} name - Fallback name to generate avatar from
 * @returns {string} Avatar URL (either the provided one or a generated fallback)
 */
export function avatarUrlWithFallback(avatarUrl, name) {
  return avatarUrl || initialsAvatarUrl(name);
}
